import React, { useRef, useState, memo } from 'react';
import {
    Typography,
    Button,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Popover,
    MenuItem,
    ListItemText,
} from '@mui/material';
import LinkIcon from '../assets/copytoClipboard.svg';
import RefreshIcon from '../assets/refresh.svg';
import MoreInfoIcon from '../assets/moreinfo.svg';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CircularProgress from '@mui/material/CircularProgress';
import { motion, useMotionValue } from 'framer-motion';
import Lottie from 'lottie-react';
import hourglass from '../assets/hourglass.json';
import pending from '../assets/PendingLottie.json';
import './InstanceCard.css';
import { useDispatch } from 'react-redux';
import { showNotification } from '../store/slices/NotificationSystemSlice';

// 1. Define the helper function first
const getLaunchButtonProps = (status) => {
    switch (status) {
        case 'STARTED':
            return { text: 'Launch', color: 'gradient', enabled: true, icon: <ChevronRightIcon sx={{ fontSize: '16px', color: 'white', background: 'black', borderRadius: '53.625px', marginLeft: '6px' }} /> };
        case 'STOPPED':
        case 'FAILED':
            return { text: 'Re Launch', color: 'failed-gradient', enabled: true, icon: <ChevronRightIcon sx={{ fontSize: '16px', color: 'black', background: 'white', borderRadius: '53.625px', marginLeft: '6px' }} /> };
        case 'PENDING':
            return {
                text: 'Pending',
                color: 'pending-bg',
                enabled: false,
                icon: (
                    <span style={{ width: 20, height: 20, display: 'inline-block' }}>
                        <Lottie animationData={pending} loop autoplay style={{ width: 20, height: 20 }} />
                    </span>
                ),
            };
        case 'INSTALLING':
            return {
                text: 'Installing',
                color: 'gray',
                enabled: false,
                icon: (
                    <span style={{ width: 20, height: 20, display: 'inline-block' }}>
                        <Lottie animationData={hourglass} loop autoplay style={{ width: 20, height: 20 }} />
                    </span>
                ),
            };
        case 'RUNNING':
            return { text: 'Running', color: 'gray', enabled: false, icon: <CircularProgress size={16} sx={{ marginLeft: '6px', color: 'white' }} /> };
        default:
            return { text: status, color: 'gray', enabled: false };
    }
};

const InstanceCard = ({
    title,
    environmentId,
    time,
    status,
    handleDeleteEnv,
    refreshInstances,
    envExports,
    onResume,
    onPause,
    onStart,
    templateinfo,
}) => {
    const [loading, setLoading] = useState(false);
    const cardRef = useRef(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [moreInfoOpen, setMoreInfoOpen] = useState(false);
    const { text, color, enabled, icon } = getLaunchButtonProps(status);

    const handleRefresh = async () => {
        try {
            const success = await refreshInstances(environmentId);
            dispatch(showNotification({
                message: success?.message === 'success'
                    ? 'Environment refreshed successfully!'
                    : 'Failed to refresh environment.',
                success: success?.message === 'success',
                duration: 2000,
                position: 'top-center',
            }));
        } catch (error) {
            dispatch(showNotification({
                message: 'Error refreshing environment.',
                success: false,
                duration: 2000,
                position: 'top-center',
            }));
        }
    };

    const handleMoreInfoClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => setAnchorEl(null);
    const openPopover = Boolean(anchorEl);


    const handleDeleteClick = () => {
        setConfirmOpen(true);
        handlePopoverClose();
    };


    const handleCopy = () => {
        if (!envExports) {
            dispatch(showNotification({
                message: 'No URL found to copy.',
                success: false,
                duration: 2000,
                position: 'top-center',
            }));
            return;
        }
        // Find the first string value that starts with http
        let url = null;
        for (const key in envExports) {
            const value = envExports[key];
            if (typeof value === 'string' && value.startsWith('http')) {
                url = value;
                break;
            }
            // If value is an object, check its values
            if (typeof value === 'object' && value !== null) {
                for (const subKey in value) {
                    const subValue = value[subKey];
                    if (typeof subValue === 'string' && subValue.startsWith('http')) {
                        url = subValue;
                        break;
                    }
                }
            }
            if (url) break;
        }
        if (url) {
            navigator.clipboard.writeText(url)
                .then(() => {
                    dispatch(showNotification({
                        message: 'URL copied to clipboard!',
                        success: true,
                        duration: 2000,
                        position: 'top-center',
                    }));
                })
                .catch((err) => {
                    console.error('Failed to copy URL: ', err);
                    dispatch(showNotification({
                        message: 'Failed to copy URL.',
                        success: false,
                        duration: 2000,
                        position: 'top-center',
                    }));
                });
        } else {
            dispatch(showNotification({
                message: 'No URL found to copy.',
                success: false,
                duration: 2000,
                position: 'top-center',
            }));
        }
    };

    const handleMoreInfoTabClick = () => {
        setMoreInfoOpen(true);
        handlePopoverClose();
    };

    const handleMoreInfoModalClose = () => setMoreInfoOpen(false);

    // Helper to extract the URL from envExports (reuse your logic from handleCopy)
    function extractUrlFromEnvExports(envExports) {
        if (!envExports) return null;

        for (const key in envExports) {
            const value = envExports[key];
            if (typeof value === 'object' && value !== null) {
                for (const subKey in value) {
                    if (subKey.toLowerCase() === 'urlproxy') {
                        const subValue = value[subKey];
                        if (typeof subValue === 'string' && subValue.startsWith('http')) {
                            return subValue;
                        }
                    }
                }
            }
        }

        // If no URLProxy found, look for URL (case-insensitive)
        for (const key in envExports) {
            const value = envExports[key];
            if (typeof value === 'object' && value !== null) {
                for (const subKey in value) {
                    if (subKey.toLowerCase() === 'url') {
                        const subValue = value[subKey];
                        if (typeof subValue === 'string' && subValue.startsWith('http')) {
                            return subValue;
                        }
                    }
                }
            }
        }

        // Fallback: any string value starting with http (original logic)
        for (const key in envExports) {
            const value = envExports[key];
            if (typeof value === 'string' && value.startsWith('http')) {
                return value;
            }
            if (typeof value === 'object' && value !== null) {
                for (const subKey in value) {
                    const subValue = value[subKey];
                    if (typeof subValue === 'string' && subValue.startsWith('http')) {
                        return subValue;
                    }
                }
            }
        }
        return null;
    }

    const handleStartClick = async () => {
        if (!enabled) return;
        setLoading(true);
        try {
            let envExportsResult = null;
            if ((status === 'FAILED' || status === 'STOPPED') && onResume) {
                envExportsResult = await onResume(environmentId);
            } else if (onStart) {
                envExportsResult = await onStart(environmentId);
            }
            const urlToOpen = extractUrlFromEnvExports(envExportsResult || envExports);
            if (urlToOpen) {
                window.open(`/playground?url=${encodeURIComponent(urlToOpen)}`, '_blank');
            } else {
                dispatch(showNotification({
                    message: 'No URL found. Please check the environment details in the More Info section.',
                    success: false,
                    duration: 3000,
                    position: 'top-center',
                }));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmClose = () => setConfirmOpen(false);
    const handleConfirmDelete = () => {
        if (handleDeleteEnv) handleDeleteEnv(environmentId);
        setConfirmOpen(false);
    };

    return (
        <motion.div
            ref={cardRef}
            style={{ perspective: 800 }}
            onMouseMove={(e) => {
                const rect = cardRef.current.getBoundingClientRect();
                const offsetX = e.clientX - rect.left - rect.width / 2;
                const offsetY = e.clientY - rect.top - rect.height / 2;
                x.set(offsetX);
                y.set(offsetY);
            }}
            onMouseLeave={() => {
                x.set(0);
                y.set(0);
            }}
        >
            <motion.div>
                <div className="instance-card">
                    <div className="card-header">
                        <div className="card-actions">
                            <Tooltip title="Copy Link" arrow placement="top">
                                <button className="icon-btn" onClick={handleCopy}>
                                    <img src={LinkIcon} alt="Copy Link" className="icon-btn-img" />
                                </button>
                            </Tooltip>
                            <Tooltip title="Refresh" arrow placement="top">
                                <button className="icon-btn" onClick={handleRefresh} disabled={loading}>
                                    <img src={RefreshIcon} alt="Refresh" className="icon-btn-img" />
                                </button>
                            </Tooltip>
                            <Tooltip title="More" arrow placement="top">
                                <button className='more-info-icon' onClick={handleMoreInfoClick}>
                                    <img src={MoreInfoIcon} alt="More Info" className="icon-btn-img" />
                                </button>
                            </Tooltip>
                            <Popover
                                open={openPopover}
                                anchorEl={anchorEl}
                                onClose={handlePopoverClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                classes={{ paper: 'custom-popover-paper' }}
                            >
                                <MenuItem onClick={handleMoreInfoTabClick}>
                                    <ListItemText className='list-item-text-custom' primary="Instance details" />
                                </MenuItem>
                                <MenuItem onClick={handleDeleteClick}>
                                    <ListItemText className='list-item-text-custom' primary="Delete" />
                                </MenuItem>
                            </Popover>
                        </div>
                    </div>
                    <div className='card-main-content'>
                        <div className="card-logo">
                            <img
                                alt={title}
                                src={templateinfo?.templateLogo}
                                style={{ height: '65px', width: '65px', borderRadius: '4px' }}
                            />
                        </div>
                        <div className="card-main">
                            <Typography variant="h6" className="card-title">{title}</Typography>
                            <Typography variant="body2" className="card-env-id">
                                Environment ID: <strong>{environmentId}</strong>
                            </Typography>

                            <div className="card-time-row">
                                <AccessTimeIcon className="card-time-icon" />
                                <span className="card-time">{time}</span>
                            </div>
                        </div>
                    </div>
                    <div className={`card-launch-row${status === 'STARTED' ? ' center-launch-row' : ''}`}>
                        <button
                            onClick={handleStartClick}
                            disabled={!enabled || loading}
                            className={`launch-btn launch-btn-animated ${color} ${status === 'STARTED' ? 'compact-launch' : ''}`}
                        >
                            <span className="launch-btn-text-container">
                                <span className="launch-btn-text-main">
                                    {text} {icon}
                                </span>
                                <span className="launch-btn-text-hover">
                                    {text} {icon}
                                </span>
                            </span>
                        </button>
                        {status === 'STARTED' && (
                            <button
                                className="stop-btn"
                                onClick={() => onPause && onPause(environmentId)}
                                title="Stop"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                                    <path d="M0 4.57143C0 2.0467 2.0467 0 4.57143 0H27.4286C29.9533 0 32 2.0467 32 4.57143V27.4286C32 29.9533 29.9533 32 27.4286 32H4.57143C2.0467 32 0 29.9533 0 27.4286V4.57143Z" fill="#5F2828" />
                                    <path d="M16 8C11.5887 8 8 11.5887 8 16C8 20.4113 11.5887 24 16 24C20.4113 24 24 20.4113 24 16C24 11.5887 20.4113 8 16 8ZM16 22C12.692 22 10 19.308 10 16C10 12.692 12.692 10 16 10C19.308 10 22 12.692 22 16C22 19.308 19.308 22 16 22ZM13.3333 13.3333H18.6667V18.6667H13.3333V13.3333Z" fill="white" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            </motion.div>
            <Dialog open={confirmOpen} onClose={handleConfirmClose} maxWidth="xs" fullWidth className="confirm-dialog">
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this environment?
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={moreInfoOpen} onClose={handleMoreInfoModalClose} maxWidth="sm" fullWidth className="more-info-dialog"
            >
                <DialogTitle>Environment Exports</DialogTitle>
                <DialogContent>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: 13, background: '#222', color: '#A0A0A0', padding: 16, borderRadius: 8 }}>
                        {JSON.stringify(envExports, null, 2)}
                    </pre>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleMoreInfoModalClose} color="primary" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </motion.div>
    );
};

// Custom comparison function for memo
const arePropsEqual = (prevProps, nextProps) => {
    return (
        prevProps.status === nextProps.status &&
        prevProps.title === nextProps.title &&
        prevProps.environmentId === nextProps.environmentId &&
        prevProps.time === nextProps.time &&
        JSON.stringify(prevProps.envExports) === JSON.stringify(nextProps.envExports)
    );
};

export default memo(InstanceCard, arePropsEqual);




